import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { ShoppingBag, Menu, X, Phone, ChevronDown } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useModals } from "@/context/ModalContext";
import { api } from "@/lib/api";
import { PHONE, PHONE_HREF, BRAND, LOGO_URL } from "@/lib/config";
import { NAV_FALLBACK } from "@/data/navFallback";

const tid = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export const Header = () => {
  const { cart, setOpen } = useCart();
  const { openConsultation } = useModals();
  const [mobile, setMobile] = useState(false);
  const [openItem, setOpenItem] = useState(null);
  const [nav, setNav] = useState(NAV_FALLBACK);

  useEffect(() => {
    let cancelled = false;
    const load = async (attempt = 0) => {
      try {
        const d = await api.getMenu();
        if (!cancelled && d?.main?.length) setNav(d.main);
      } catch {
        if (!cancelled && attempt < 3) setTimeout(() => load(attempt + 1), 1500 * (attempt + 1));
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  return (
    <header className="glass fixed top-0 inset-x-0 z-50 border-b border-white/10">
      <div className="max-w-[1500px] mx-auto px-5 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="brand-logo" className="flex items-center shrink-0">
          <img src={LOGO_URL} alt={BRAND} className="h-10 sm:h-11 w-auto object-contain" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1 text-sm">
          {nav.map((n) => {
            const hasMenu = (n.columns || []).length > 0;
            return (
              <div key={n.title} className="relative"
                onMouseEnter={() => setOpenItem(n.title)}
                onMouseLeave={() => setOpenItem(null)}>
                <Link to={n.to} data-testid={`nav-${tid(n.title)}`}
                  className="flex items-center gap-1 text-cream/80 hover:text-gold transition-colors duration-300 px-3 py-2">
                  {n.title}
                  {hasMenu && <ChevronDown size={13} />}
                </Link>
                {hasMenu && openItem === n.title && (
                  <div className="absolute top-full left-0 bg-charcoal border border-white/10 shadow-2xl flex gap-8 px-7 py-6"
                    data-testid={`megamenu-${tid(n.title)}`}>
                    {n.columns.map((col, ci) => (
                      <div key={ci} className="min-w-[150px]">
                        <p className="overline text-cream/40 mb-3 whitespace-nowrap">{col.heading}</p>
                        <ul className="space-y-2">
                          {col.items.map((it) => (
                            <li key={it.title}>
                              <Link to={it.to} data-testid={`nav-sub-${tid(it.title)}`}
                                className="block text-sm text-cream/80 hover:text-gold transition-colors whitespace-nowrap">
                                {it.title}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="flex items-center gap-3 sm:gap-4">
          <a href={PHONE_HREF} data-testid="header-phone"
            className="hidden xl:flex items-center gap-2 text-cream/70 hover:text-gold text-sm transition-colors">
            <Phone size={15} /> {PHONE}
          </a>
          <button data-testid="header-cart-btn" onClick={() => setOpen(true)}
            className="relative text-cream hover:text-gold transition-colors">
            <ShoppingBag size={22} />
            {cart.itemCount > 0 && (
              <span data-testid="cart-count"
                className="absolute -top-2 -right-2 bg-gold text-espresso text-[10px] font-semibold min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1">
                {cart.itemCount}
              </span>
            )}
          </button>
          <button className="lg:hidden text-cream" data-testid="mobile-menu-btn"
            onClick={() => setMobile((m) => !m)}>
            {mobile ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {mobile && (
        <div className="lg:hidden glass border-t border-white/10 px-5 py-4 flex flex-col gap-1 max-h-[80vh] overflow-y-auto">
          {nav.map((n) => (
            <div key={n.title} className="border-b border-white/5 pb-2 mb-1">
              <Link to={n.to} onClick={() => setMobile(false)}
                data-testid={`mobile-nav-${tid(n.title)}`}
                className="block text-cream/90 hover:text-gold py-2 text-sm font-medium">
                {n.title}
              </Link>
              {(n.columns || []).map((col, ci) => (
                <div key={ci} className="pl-3 pb-1">
                  {col.heading && col.heading.trim() && (
                    <p className="text-[10px] uppercase tracking-wider text-gold/70 mt-1">{col.heading}</p>
                  )}
                  {col.items.map((it) => (
                    <Link key={it.title} to={it.to} onClick={() => setMobile(false)}
                      className="block text-cream/60 hover:text-gold py-1 pl-2 text-sm">
                      {it.title}
                    </Link>
                  ))}
                </div>
              ))}
            </div>
          ))}
          <button data-testid="mobile-expert-btn"
            onClick={() => { setMobile(false); openConsultation(); }}
            className="mt-2 text-left text-gold text-sm py-2">Talk to an Expert</button>
        </div>
      )}
    </header>
  );
};
